import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Loader2, LogOut, CreditCard, CheckCircle, AlertCircle, Save, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const StripeSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [showSecrets, setShowSecrets] = useState({
    secret_key: false,
    webhook_secret: false
  });
  
  const [formData, setFormData] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    test_mode: true
  });

  useEffect(() => {
    fetchStripeConfig();
  }, []);

  const fetchStripeConfig = async () => {
    try {
      const response = await api.get('/admin/stripe/config');
      if (response.data && response.data.configured !== false) {
        setStripeConfig(response.data);
        setFormData({
          publishable_key: response.data.publishable_key || '',
          secret_key: '', // Don't populate for security
          webhook_secret: '', // Don't populate for security
          test_mode: response.data.test_mode ?? true
        });
      }
    } catch (error) {
      console.error('Failed to fetch Stripe config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTestModeToggle = (checked) => {
    setFormData(prev => ({
      ...prev,
      test_mode: checked
    }));
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.publishable_key || !formData.secret_key || !formData.webhook_secret) {
      setMessage({
        type: 'error',
        text: 'All fields are required'
      });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);
      
      await api.post('/admin/stripe/configure', formData);
      
      setMessage({
        type: 'success',
        text: 'Stripe configuration saved successfully!'
      });
      
      // Refresh config
      await fetchStripeConfig();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to save Stripe configuration'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                alt="SlickSignature"
                className="h-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                }}
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-gray-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="outline"
          onClick={handleBackToAdmin}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Portal
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <CreditCard className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Stripe Configuration</CardTitle>
                <CardDescription>
                  Connect your Stripe account to process subscription payments
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Banner */}
            {stripeConfig ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Stripe is configured and ready to process payments in {stripeConfig.test_mode ? 'test' : 'live'} mode
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Stripe is not configured. Please enter your API keys below to start accepting payments.
                </AlertDescription>
              </Alert>
            )}

            {/* Message Alert */}
            {message && (
              <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Test Mode Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div>
                  <Label className="text-base font-semibold">Test Mode</Label>
                  <p className="text-sm text-gray-600">
                    Use test API keys for development. Switch to live mode for production.
                  </p>
                </div>
                <Switch
                  checked={formData.test_mode}
                  onCheckedChange={handleTestModeToggle}
                />
              </div>

              {/* Publishable Key */}
              <div>
                <Label htmlFor="publishable_key">
                  Publishable Key *
                  <span className="text-xs text-gray-500 ml-2">
                    (Starts with pk_test_ or pk_live_)
                  </span>
                </Label>
                <Input
                  id="publishable_key"
                  name="publishable_key"
                  value={formData.publishable_key}
                  onChange={handleInputChange}
                  placeholder={formData.test_mode ? 'pk_test_...' : 'pk_live_...'}
                  required
                />
              </div>

              {/* Secret Key */}
              <div>
                <Label htmlFor="secret_key">
                  Secret Key *
                  <span className="text-xs text-gray-500 ml-2">
                    (Starts with sk_test_ or sk_live_)
                  </span>
                </Label>
                <div className="relative">
                  <Input
                    id="secret_key"
                    name="secret_key"
                    type={showSecrets.secret_key ? 'text' : 'password'}
                    value={formData.secret_key}
                    onChange={handleInputChange}
                    placeholder={formData.test_mode ? 'sk_test_...' : 'sk_live_...'}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('secret_key')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets.secret_key ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Keep this secret! Never share it publicly.
                </p>
              </div>

              {/* Webhook Secret */}
              <div>
                <Label htmlFor="webhook_secret">
                  Webhook Secret *
                  <span className="text-xs text-gray-500 ml-2">
                    (Starts with whsec_)
                  </span>
                </Label>
                <div className="relative">
                  <Input
                    id="webhook_secret"
                    name="webhook_secret"
                    type={showSecrets.webhook_secret ? 'text' : 'password'}
                    value={formData.webhook_secret}
                    onChange={handleInputChange}
                    placeholder="whsec_..."
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('webhook_secret')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets.webhook_secret ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Get this from your Stripe webhook settings
                </p>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </form>

            {/* Instructions */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold mb-3">How to get your Stripe API keys:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Log in to your <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Dashboard</a></li>
                <li>Click on "Developers" in the left sidebar</li>
                <li>Go to "API keys" to find your Publishable and Secret keys</li>
                <li>Go to "Webhooks" and create a new webhook endpoint</li>
                <li>Set the endpoint URL to: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{window.location.origin}/api/webhooks/stripe</code></li>
                <li>Select events: <code className="bg-gray-100 px-1 rounded text-xs">checkout.session.completed, customer.subscription.*, invoice.*</code></li>
                <li>Copy the webhook signing secret</li>
              </ol>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  ⚠️ Important: Webhook Configuration
                </p>
                <p className="text-xs text-blue-700">
                  Your webhook endpoint URL: <br />
                  <code className="bg-blue-100 px-2 py-1 rounded mt-1 inline-block">{window.location.origin}/api/webhooks/stripe</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StripeSettings;
